import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { notification } from "antd";

import {
  useExportOrganizationDataMutation,
  useLazyGetExportedOrganizationDataQuery,
} from "@/redux/service";
import { RootState } from "@/redux/store";
import { clearDownload, setDownloadState } from "@/redux/downloads/reducer";
import { NotificationDescription } from "@/components_v2/Notification/NotificationDescription";
import { downloadPollingService } from "@/shared/services/downloadPollingService";
import { UseOrganizationExportDownloadOptions } from "@/features/organizations/profile/types";

export const useOrganizationExportDownload = (
  options: UseOrganizationExportDownloadOptions = {},
) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [getExportedOrganizationData] =
    useLazyGetExportedOrganizationDataQuery();
  const [exportOrganizationData] = useExportOrganizationDataMutation();

  const downloadingStates = useSelector(
    (state: RootState) => state.downloads.downloadingStates,
  );

  const { pollInterval = 2000, timeout = 300000 } = options;

  const showSuccessNotification = useCallback(
    (link: string) => {
      notification.success({
        message: (
          <div>
            <p>
              {t("organizations.labels.exportReady")}
              {t("organizations.labels.downloadDoesntStartAutomatically")},{" "}
              <a href={link} target="_blank" download>
                {t("organizations.labels.clickHere")}
              </a>{" "}
              {t("organizations.labels.toDownload")}
            </p>
          </div>
        ),
        placement: "bottomRight",
        duration: 30,
      });
    },
    [t],
  );

  const showErrorNotification = useCallback(() => {
    notification.error({
      message: t("organizations.labels.exportTimeout"),
      placement: "bottomRight",
    });
  }, [t]);

  const showTimeoutNotification = useCallback(() => {
    notification.warning({
      message: t("organizations.labels.exportTimeout"),
      placement: "bottomRight",
    });
  }, [t]);

  const handleCleanup = useCallback(
    (organizationId: string) => {
      dispatch(clearDownload(organizationId));
    },
    [dispatch],
  );

  const exportOrganization = useCallback(
    async (organizationId: number, organizationName?: string) => {
      if (downloadingStates[organizationId]) {
        return;
      }

      dispatch(
        setDownloadState({
          id: organizationId.toString(),
          isDownloading: true,
        }),
      );

      const createNotificationDescription = (filename: string) => (
        <NotificationDescription
          filename={filename}
          descriptionKey="organizations.labels.generatingExportDescription"
        />
      );

      const exportFilename = organizationName
        ? `${organizationName} Export.csv`
        : "Organization Export.csv";

      await exportOrganizationData(organizationId).unwrap();

      await downloadPollingService.initiateDownload(
        organizationId.toString(),
        getExportedOrganizationData,
        { pollInterval, timeout },
        {
          onSuccess: (response) => {
            dispatch(
              setDownloadState({
                id: organizationId.toString(),
                isDownloading: false,
              }),
            );
            showSuccessNotification(response.link);
          },
          onError: () => {
            dispatch(
              setDownloadState({
                id: organizationId.toString(),
                isDownloading: false,
              }),
            );
            showErrorNotification();
          },
          onTimeout: () => {
            showTimeoutNotification();
          },
          onCleanup: handleCleanup,
        },
        {
          message: t("organizations.labels.generatingExportTitle"),
          description: createNotificationDescription(exportFilename),
        },
      );
    },
    [
      downloadingStates,
      getExportedOrganizationData,
      dispatch,
      t,
      pollInterval,
      timeout,
      showSuccessNotification,
      showErrorNotification,
      showTimeoutNotification,
      handleCleanup,
    ],
  );

  const isExporting = useCallback(
    (organizationId: number) =>
      downloadingStates[organizationId.toString()] || false,
    [downloadingStates],
  );

  return {
    startDownload: exportOrganization,
    isDownloading: isExporting,
  };
};
